import { html } from "lit";
import { customElement, queryAssignedElements, state } from "lit/decorators.js";
import { StyleInfo, styleMap } from "lit/directives/style-map.js";

import FormControl, { FormControlProps } from "@/components/form/form-control";
import AtlasToggleItem from "@/components/form/atlas-toggle-item/atlas-toggle-item";

import { Watch } from "@/decorators/watch";
import { emit } from "@/internals/events";

import styles from "./atlas-toggle.scss";

export type ToggleProps = FormControlProps;

/**
 * O componente de toggle é um componente que permite ao usuário escolher entre duas opções.
 *
 * @event {CustomEvent} atlas-toggle-change - Evento disparado quando o valor do toggle é alterado
 *
 * @slot - Opções do toggle
 *
 * @tag atlas-toggle
 */
@customElement("atlas-toggle")
export default class AtlasToggle extends FormControl {
    static styles = styles;

    @state() selectedItem: AtlasToggleItem;

    @state() private _checkedActiveMarkStyles: StyleInfo = {};

    private _initialHelperText: string = "";

    private _resizeObserver: ResizeObserver | null = null;

    @queryAssignedElements({ selector: "atlas-toggle-item", flatten: true })
    public toggleItems!: Array<AtlasToggleItem>;

    constructor() {
        super();

        this.toggleActiveItem = this.toggleActiveItem.bind(this);
        this.onElementUpdate = this.onElementUpdate.bind(this);
    }

    public connectedCallback() {
        super.connectedCallback?.();

        this.addEventListener("atlas-toggle-item-check", this.toggleActiveItem);
        this.addEventListener("atlas-element-update", this.onElementUpdate);

        this.updateComplete.then(() => {
            this._initialHelperText = this.helperText;

            if (this.value) this.syncValue();
            else this.findSelectedItem();

            if (this.disabled) this.syncDisabled();

            this.registerResizeObserver();
        });
    }

    public disconnectedCallback() {
        super.disconnectedCallback?.();

        this.removeEventListener("atlas-toggle-item-check", this.toggleActiveItem);
        this.removeEventListener("atlas-element-update", this.onElementUpdate);

        this._resizeObserver?.disconnect();
    }

    public async getUpdateComplete() {
        await super.getUpdateComplete();
        await Promise.all(this.toggleItems.map((element) => element.updateComplete));
        return true;
    }

    /** @internal */
    @Watch("value", true)
    public syncValue() {
        this.toggleItems.forEach((item) => {
            if (this.value === item.value) {
                item.check();
            } else {
                item.uncheck();
            }
        });

        emit(this, "atlas-toggle-change");
        emit(this, "atlas-form-element-touch");
    }

    /** @internal */
    @Watch("disabled", true)
    public syncDisabled() {
        this.toggleItems.forEach((item) => {
            item.toggleAttribute("disabled", this.disabled);
        });

        this.moveActiveMark();
    }

    private onElementUpdate(event: CustomEvent) {
        const changedProperties: Map<string, any> = event.detail;

        if (changedProperties?.has("disabled")) this.moveActiveMark();
    }

    private toggleActiveItem(event: CustomEvent) {
        this.shadowRoot.querySelector(".checked-active-mark")?.classList.add("transition");
        const element = event.target as AtlasToggleItem;
        this.setSelectedItem(element);
        this.moveActiveMark();
    }

    private moveActiveMark() {
        const element = this.selectedItem;

        if (!element) return;

        const toggleRect = this.getBoundingClientRect();
        const rect = element.shadowRoot.querySelector("label").getBoundingClientRect();
        const offsetLeft = rect.left - toggleRect.left - 1;

        this._checkedActiveMarkStyles = {
            width: `${rect.width}px`,
            transform: `translateX(${offsetLeft}px)`,
            opacity: this.disabled || element.disabled ? "0.5" : "1"
        };

        setTimeout(() => {
            this.shadowRoot.querySelector(".checked-active-mark")?.classList.remove("transition");
        }, 300);
    }

    private setSelectedItem(selectedItem: AtlasToggleItem) {
        this.selectedItem = selectedItem;
        this.value = selectedItem.value;

        if (selectedItem.helperText) this.helperText = selectedItem.helperText;
        else this.helperText = this._initialHelperText;
    }

    private findSelectedItem() {
        this.toggleItems.forEach((item) => {
            if (item.checked) this.setSelectedItem(item);
        });
    }

    protected renderCheckedMark() {
        return html`<div class="checked-active-mark" style=${styleMap(this._checkedActiveMarkStyles)}></div>`;
    }

    private async registerResizeObserver() {
        await this.updateComplete;

        this._resizeObserver = new ResizeObserver(async () => {
            this.moveActiveMark();
        });

        this._resizeObserver.observe(this);
    }

    /** @internal */
    public render() {
        const btnGroupStyles = {
            "grid-template-columns": `repeat(${this.toggleItems.length}, 1fr)`
        };

        return html`
            ${this.renderLabel()}
            <div class="atlas-toggle">
                <div class="btn-group" style=${styleMap(btnGroupStyles)}>
                    <slot></slot>

                    ${this.renderCheckedMark()}
                </div>
                ${this.renderHelperText({ isToggleHelper: true })}
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-toggle": AtlasToggle;
    }
}
